/**
 * dndConstraints — Centralized DnD constraint matrix (§33).
 *
 * Provides `canDropOnSidebarTarget` to determine whether a drag source
 * can be dropped on a given sidebar target. Used by useSidebarDragOver to
 * suppress visual highlight on invalid targets, and by sidebarDropHandler
 * as a safety check.
 *
 * The constraint logic that was previously scattered across
 * sidebarDropHandler, DndListProvider, and GroupedTaskList is now
 * centralized here as the single source of truth.
 */

import { ObjectType } from "@/db/schema"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DragSourceInfo {
  /** Primary type of the dragged item (section, task, project, etc.) */
  type: ObjectType
  /** True when the drag set contains sections (section block drag) */
  hasSections: boolean
  /** True when the drag set contains projects or lists */
  hasContainers: boolean
}

// ---------------------------------------------------------------------------
// Sidebar target categories
// ---------------------------------------------------------------------------

/** Date-scheduling targets — change startAt/startAtType */
const DATE_TARGETS = new Set([
  "today",
  "this-evening",
  "anyday",
  "someday",
  "upcoming",
])

/** Container targets — move into a project/list */
function isContainerTarget(target: string): boolean {
  return target.startsWith("project:") || target.startsWith("list:")
}

/** Area targets — move into an area (only valid for containers) */
function isAreaTarget(target: string): boolean {
  return target.startsWith("area:")
}

// ---------------------------------------------------------------------------
// Constraint matrix
// ---------------------------------------------------------------------------

/**
 * Determine if a drag source can be dropped on a sidebar target.
 *
 * Rules (§33):
 * | Source type       | Date targets | Inbox | Container | Area | Logbook | Trash |
 * |-------------------|-------------|-------|-----------|------|---------|-------|
 * | Task              | ✅          | ✅    | ✅        | ❌   | ✅      | ✅    |
 * | Section (block)  | ❌          | ✅*   | ✅        | ❌   | ❌      | ✅    |
 * | Project / List    | ✅ (sched)  | ❌    | ❌        | ✅   | ✅      | ✅    |
 * | Checklist/Subtask | ❌          | ❌    | ❌        | ❌   | ❌      | ❌    |
 *
 * Notes:
 * - Section → Inbox: children move to inbox, section is soft-deleted
 * - Section → Date targets: no-op (sections aren't scheduled)
 * - Section → Logbook: no-op (sections can't be completed)
 * - Project/List → Container: can't nest containers
 * - Project/List → Inbox: doesn't make sense (containers live in areas)
 * - Checklist/Subtask: never draggable to sidebar
 */
export function canDropOnSidebarTarget(
  source: DragSourceInfo,
  target: string,
): boolean {
  const { type, hasSections, hasContainers } = source

  // Checklists and subtasks can never drop on sidebar
  if (type === ObjectType.Checklist || type === ObjectType.Subtask) {
    return false
  }

  // ── Pure section drag ──
  if (type === ObjectType.Section || hasSections) {
    // Sections can only go to: container targets, inbox (delete section, keep children), trash
    if (DATE_TARGETS.has(target)) return false
    if (target === "logbook") return false
    if (isAreaTarget(target)) return false
    // inbox, container, trash are valid
    return true
  }

  // ── Container drag (project/list) ──
  if (
    type === ObjectType.Project ||
    type === ObjectType.List ||
    hasContainers
  ) {
    // Containers can't nest into other containers
    if (isContainerTarget(target)) return false
    // Containers can't go to inbox (they live in areas)
    if (target === "inbox") return false
    // Containers CAN go to: date targets (schedule), area, logbook, trash
    return true
  }

  // ── Regular task drag ──
  // Tasks can go to everything except areas
  if (isAreaTarget(target)) return false

  return true
}

// ---------------------------------------------------------------------------
// Convenience: extract DragSourceInfo from a list of ObjectTypes
// ---------------------------------------------------------------------------

/**
 * Build a DragSourceInfo from an array of object types being dragged.
 * Uses the "primary" type (the first item) as the main type.
 */
export function buildDragSourceInfo(types: ObjectType[]): DragSourceInfo {
  const primary = types[0] ?? ObjectType.Task
  return {
    type: primary,
    hasSections: types.some((t) => t === ObjectType.Section),
    hasContainers: types.some(
      (t) => t === ObjectType.Project || t === ObjectType.List,
    ),
  }
}
